import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Clock,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  Loader2,
  ArrowRight,
  Activity
} from 'lucide-react';
import apiService from '../services/api';

interface CompanyStats {
  totalComplaints: number;
  pendingComplaints: number;
  inProgressComplaints: number;
  resolvedComplaints: number;
  closedComplaints: number;
  avgResolutionTime: number;
  companyName: string;
}

const statusColorMap: Record<string, string> = {
  pending: 'bg-orange-100 text-orange-800 border-orange-200',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
  resolved: 'bg-green-100 text-green-800 border-green-200',
  closed: 'bg-gray-100 text-gray-800 border-gray-200',
};

const CompanyDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [stats, setStats] = useState<CompanyStats | null>(null);
  const [recentComplaints, setRecentComplaints] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const user = apiService.getCurrentUser();

  const fetchCompanyStats = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch complaints for the company (no limit — use large page size for stats)
      const response = await apiService.getComplaints(undefined, 1, 1000);
      const complaints = response.results;

      // Calculate stats
      const totalComplaints = complaints.length;
      const pendingComplaints = complaints.filter((c: any) => c.status === 'pending').length;
      const inProgressComplaints = complaints.filter((c: any) => c.status === 'in_progress').length;
      const resolvedComplaints = complaints.filter((c: any) => c.status === 'resolved').length;
      const closedComplaints = complaints.filter((c: any) => c.status === 'closed').length;

      // Store the 5 most recent complaints for display
      const sorted = [...complaints].sort((c: any, b: any) =>
        new Date(b.createdAt).getTime() - new Date(c.createdAt).getTime()
      );
      setRecentComplaints(sorted.slice(0, 5));

      setStats({
        totalComplaints,
        pendingComplaints,
        inProgressComplaints,
        resolvedComplaints,
        closedComplaints,
        avgResolutionTime: 0,
        companyName: user?.firstName || 'Company'
      });
      setError('');
    } catch (err) {
      setError(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [t, user?.firstName]);

  useEffect(() => {
    fetchCompanyStats();
  }, [fetchCompanyStats]);

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-50/30 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500 font-medium">{isRTL ? 'جاري التحميل...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-50/30 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border-l-4 border-red-500 rounded-2xl p-6 flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-800 font-medium">{error || t('common.error')}</p>
          </div>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: t('dashboard.totalComplaints'),
      value: stats.totalComplaints,
      icon: FileText,
      gradient: 'from-brand-500 to-brand-600',
      iconBg: 'bg-brand-100',
      iconColor: 'text-brand-600',
      change: '',
      trending: 'neutral' as const,
    },
    {
      title: t('dashboard.pendingComplaints'),
      value: stats.pendingComplaints,
      icon: Clock,
      gradient: 'from-orange-500 to-orange-600',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      change: '',
      trending: 'neutral' as const,
    },
    {
      title: t('complaints.statusOptions.in_progress'),
      value: stats.inProgressComplaints,
      icon: Activity,
      gradient: 'from-blue-500 to-blue-600',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      change: '',
      trending: 'neutral' as const,
    },
    {
      title: t('dashboard.resolved'),
      value: stats.resolvedComplaints + stats.closedComplaints,
      icon: CheckCircle,
      gradient: 'from-green-500 to-green-600',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      change: '',
      trending: 'neutral' as const,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-50/30 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Premium Header */}
        <div className="relative overflow-hidden rounded-3xl shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-r from-brand-600 via-brand-700 to-emerald-700 opacity-95"></div>
          <div className="absolute inset-0 backdrop-blur-3xl bg-white/10"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -mr-48 -mt-48"></div>
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-brand-800/20 rounded-full -ml-36 -mb-36"></div>

          <div className="relative px-8 py-10">
            <h1 className="text-4xl font-black text-white mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
              {t('dashboard.welcome') || 'Welcome'}, {stats.companyName}
            </h1>
            <p className="text-brand-50 text-lg font-medium" dir={isRTL ? 'rtl' : 'ltr'}>
              {t('dashboard.companyOverview') || 'Your complaints overview and management'}
            </p>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 rounded-2xl p-4 flex items-start gap-3 animate-fadeIn">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-800 font-medium">{error}</p>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((card, index) => (
            <div
              key={index}
              className="group relative bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 ${card.iconBg} rounded-xl`}>
                    <card.icon className={`w-6 h-6 ${card.iconColor}`} />
                  </div>
                </div>
                <h3 className="text-sm font-semibold text-gray-600 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                  {card.title}
                </h3>
                <p className="text-3xl font-black text-gray-800 mb-1">
                  {card.value.toLocaleString()}
                </p>
              </div>
              <div className={`h-1 bg-gradient-to-r ${card.gradient}`}></div>
            </div>
          ))}
        </div>

        {/* Recent Complaints Card */}
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="px-8 py-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-black text-gray-800" dir={isRTL ? 'rtl' : 'ltr'}>
                  {t('dashboard.recentComplaints') || 'Recent Complaints'}
                </h2>
                <p className="text-gray-500 mt-1" dir={isRTL ? 'rtl' : 'ltr'}>
                  {t('dashboard.recentComplaintsDesc') || 'Your latest submitted complaints'}
                </p>
              </div>
              <button
                onClick={() => navigate('/complaints')}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-700 hover:to-brand-800 text-white font-bold rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <span>{t('common.viewAll') || 'View All'}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="p-8">
            {recentComplaints.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-10 h-10 text-gray-400" />
                </div>
                <p className="text-gray-500 font-medium">
                  {t('dashboard.noComplaints') || 'No complaints assigned yet'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentComplaints.map((complaint: any) => (
                  <div
                    key={complaint.id || complaint._id}
                    className="group flex items-center justify-between p-4 rounded-2xl border border-gray-100 hover:border-brand-200 hover:bg-brand-50/30 cursor-pointer transition-all duration-300"
                    onClick={() => navigate(`/complaints/${complaint.id || complaint._id}`)}
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="p-2 bg-brand-100 rounded-xl group-hover:bg-brand-200 transition-colors">
                        <FileText className="w-5 h-5 text-brand-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-800 truncate" dir={isRTL ? 'rtl' : 'ltr'}>
                          {complaint.locationName || complaint.description || t('common.noData') || 'N/A'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {complaint.createdAt
                            ? new Date(complaint.createdAt).toLocaleDateString()
                            : ''}
                        </p>
                      </div>
                    </div>
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-xl text-xs font-bold border ${statusColorMap[complaint.status] || 'bg-gray-100 text-gray-600'}`}>
                      {t(`complaints.statusOptions.${complaint.status}`) || complaint.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div
            className="group relative bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden cursor-pointer hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
            onClick={() => navigate('/complaints')}
          >
            <div className="p-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-brand-100 rounded-xl group-hover:bg-brand-200 transition-colors">
                  <FileText className="w-6 h-6 text-brand-600" />
                </div>
                <h3 className="text-xl font-black text-gray-800" dir={isRTL ? 'rtl' : 'ltr'}>
                  {t('menu.complaints')}
                </h3>
              </div>
              <p className="text-gray-600" dir={isRTL ? 'rtl' : 'ltr'}>
                {t('dashboard.manageComplaintsDesc') || 'View and manage all your complaints'}
              </p>
            </div>
            <div className="h-1 bg-gradient-to-r from-brand-500 to-brand-600"></div>
          </div>
        </div>

        {/* Important Information Card */}
        <div className="bg-gradient-to-br from-brand-50 to-emerald-50 rounded-3xl shadow-xl border border-brand-100 overflow-hidden">
          <div className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-brand-600 rounded-xl">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-black text-gray-800" dir={isRTL ? 'rtl' : 'ltr'}>
                {t('dashboard.importantInfo') || 'Important Information'}
              </h2>
            </div>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 bg-brand-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
                <span className="text-gray-700 font-medium" dir={isRTL ? 'rtl' : 'ltr'}>
                  {t('dashboard.info1') || 'Complaints are reviewed within 24 hours'}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 bg-brand-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
                <span className="text-gray-700 font-medium" dir={isRTL ? 'rtl' : 'ltr'}>
                  {t('dashboard.info2') || 'You will receive notifications on complaint status updates'}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 bg-brand-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
                <span className="text-gray-700 font-medium" dir={isRTL ? 'rtl' : 'ltr'}>
                  {t('dashboard.info3') || 'Provide detailed information for faster resolution'}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyDashboardPage;
